import { useState } from "react";
import type { Bootstrap, SavingsGoal } from "../../shared/types";
import { api, type SavingInput } from "../lib/api";
import { parseMoney } from "../lib/format";
import Modal from "./Modal";
import Select from "./Select";
import { useConfirm } from "./ConfirmProvider";

// Add or edit a savings goal (with delete when editing).
export default function SavingsModal({
  data,
  goal,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  goal: SavingsGoal | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = goal != null;
  const [name, setName] = useState(goal?.name ?? "");
  const [target, setTarget] = useState(goal ? (goal.targetMinor / 100).toFixed(2) : "");
  const [current, setCurrent] = useState(goal ? (goal.currentMinor / 100).toFixed(2) : "");
  const [startDate, setStartDate] = useState(goal?.startDate ?? "");
  const [targetDate, setTargetDate] = useState(goal?.targetDate ?? "");
  const [memberId, setMemberId] = useState(goal?.memberId ? String(goal.memberId) : "");
  const [bank, setBank] = useState(goal?.bank ?? "");
  const [description, setDescription] = useState(goal?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const targetMinor = parseMoney(target);
    if (!name.trim()) return setError("Give the goal a name");
    if (targetMinor == null || targetMinor <= 0) return setError("Enter a target amount");
    const currentMinor = current.trim() ? parseMoney(current) : 0;
    if (currentMinor == null || currentMinor < 0) return setError("Saved so far must be a valid amount");
    if (startDate && targetDate && targetDate < startDate) return setError("Save-by date must be after the start date");

    const payload: SavingInput = {
      name: name.trim(),
      targetMinor,
      currentMinor,
      startDate: startDate || null,
      targetDate: targetDate || null,
      description: description.trim() || null,
      memberId: memberId ? Number(memberId) : null,
      bank: bank.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) await api.updateSaving(goal.id, payload);
      else await api.createSaving(payload);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  const ask = useConfirm();

  async function remove() {
    if (!goal) return;
    if (!(await ask({ title: "Delete goal?", message: `Delete goal "${goal.name}"? This can't be undone.`, confirmLabel: "Delete", danger: true }))) return;
    setSaving(true);
    try {
      await api.deleteSaving(goal.id);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={editing ? "Edit goal" : "New savings goal"} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span className="field__label">Goal name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New car, Holiday" autoFocus />
        </label>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Target (£)</span>
            <input className="input" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="5000.00" />
          </label>
          <label className="field">
            <span className="field__label">Saved so far (£)</span>
            <input className="input" inputMode="decimal" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="0.00" />
          </label>
        </div>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Start date</span>
            <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Save by</span>
            <input className="input" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </label>
        </div>
        <div className="form__row">
          <div className="field">
            <span className="field__label">Whose account</span>
            <Select
              value={memberId}
              onChange={setMemberId}
              placeholder="—"
              ariaLabel="Account holder"
              options={[{ value: "", label: "—" }, ...data.household.members.map((m) => ({ value: String(m.id), label: m.name }))]}
            />
          </div>
          <label className="field">
            <span className="field__label">Bank</span>
            <input className="input" value={bank} onChange={(e) => setBank(e.target.value)} placeholder="e.g. HSBC, Lloyds" />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Description</span>
          <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's it for?" />
        </label>

        {error && <p className="form__error">{error}</p>}
        <div className="form__actions form__actions--split">
          {editing ? <button className="btn btn--danger" onClick={remove} disabled={saving}>Delete</button> : <span />}
          <div className="form__actions">
            <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn--primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editing ? "Save" : "Add goal"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
